import React, { useEffect, useState } from 'react';
import {
  RefreshCw, ShieldCheck, Database, CheckCircle2, Upload, FileSpreadsheet,
  Trash2, History, ArrowRight, Loader2, Landmark,
} from 'lucide-react';
import api, { errorText } from '../config';

const fmtUsd = (v) => {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return '—';
  const n = Number(v);
  if (Math.abs(n) >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
  if (Math.abs(n) >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
  return `$${n.toFixed(2)}`;
};

const prettyKey = (k) => k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

export default function Normalization() {
  const [portcos, setPortcos] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [portcoId, setPortcoId] = useState('');
  const [period, setPeriod] = useState('');
  const [currency, setCurrency] = useState('USD');

  const [fileInfo, setFileInfo] = useState(null);
  const [lineItems, setLineItems] = useState([]);
  const [skipped, setSkipped] = useState([]);
  const [parseBusy, setParseBusy] = useState(false);

  const [normalizing, setNormalizing] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const [history, setHistory] = useState([]);
  const [cmpA, setCmpA] = useState('');
  const [cmpB, setCmpB] = useState('');
  const [compareBusy, setCompareBusy] = useState(false);
  const [comparison, setComparison] = useState(null);

  useEffect(() => {
    api.get('/api/portfolio/summary').then(r => {
      const list = (r.data.portco_list || []).filter(p => p.status !== 'exited');
      setPortcos(list);
      if (list.length && !portcoId) setPortcoId(list[0].portco_id);
    }).catch(() => setPortcos([]));
    api.get('/api/normalization/currencies').then(r => setCurrencies(r.data.supported_currencies || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadHistory = (pid) => {
    setHistory([]); setComparison(null); setCmpA(''); setCmpB('');
    if (!pid) return;
    api.get(`/api/normalization/portco/${pid}/history`)
      .then(r => {
        setHistory(r.data || []);
        if ((r.data || []).length >= 2) { setCmpA(r.data[1].period); setCmpB(r.data[0].period); }
      })
      .catch(() => setHistory([]));
  };

  useEffect(() => { loadHistory(portcoId); }, [portcoId]); // eslint-disable-line react-hooks/exhaustive-deps

  const parseFile = async (file) => {
    setParseBusy(true); setError(null); setResult(null); setSkipped([]); setFileInfo(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/api/normalization/parse-csv', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setFileInfo({ name: res.data.filename, rows: res.data.row_count, total: res.data.total_raw });
      setLineItems(res.data.line_items || []);
      setSkipped(res.data.skipped || []);
      if (!period) setPeriod(`FY${new Date().getFullYear()}`);
    } catch (err) {
      setError(errorText(err, 'Could not parse the CSV file.'));
    } finally {
      setParseBusy(false);
    }
  };

  const normalize = async () => {
    if (!portcoId || !period || !lineItems.length) return;
    setNormalizing(true); setError(null); setResult(null);
    try {
      const res = await api.post('/api/normalization/normalize', {
        raw_data: lineItems.map(li => ({ account_name: li.account_name, amount: Number(li.amount) })),
        portco_id: portcoId,
        period,
        source_currency: currency,
      });
      setResult(res.data);
      loadHistory(portcoId);
    } catch (err) {
      setError(errorText(err, 'Normalization failed.'));
    } finally {
      setNormalizing(false);
    }
  };

  const runCompare = async () => {
    if (!portcoId || !cmpA || !cmpB || cmpA === cmpB) return;
    setCompareBusy(true); setComparison(null); setError(null);
    try {
      const res = await api.post('/api/normalization/compare', {
        portco_id: portcoId, period_a: cmpA, period_b: cmpB,
      });
      setComparison({ a: cmpA, b: cmpB, changes: res.data.changes || {} });
    } catch (err) {
      setError(errorText(err, 'Comparison failed.'));
    } finally {
      setCompareBusy(false);
    }
  };

  const removeRow = (i) => setLineItems(lineItems.filter((_, idx) => idx !== i));

  const topMetrics = result ? [
    { label: 'Total Revenue', value: result.total_revenue },
    { label: 'COGS', value: result.total_cogs },
    { label: 'Gross Profit', value: result.gross_profit },
    { label: 'Operating Expenses', value: result.total_opex },
    { label: 'EBITDA', value: result.ebitda, highlight: true },
    { label: 'EBITDA Margin', value: `${result.ebitda_margin_pct}%`, pct: true },
  ] : [];

  return (
    <div className="page-container">
      <div>
        <h2 className="page-title">Standardize Financials</h2>
        <p className="page-subtitle">Upload any financial spreadsheet — Caply turns it into clean, comparable numbers</p>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-lg border border-red-200 bg-red-50 text-sm text-red-700">{error}</div>
      )}

      {/* Step 1 — basics */}
      <div className="card mb-4">
        <div className="card-header">
          <h3 className="card-title"><Landmark className="w-5 h-5 text-brand-600" />1 · Company &amp; period</h3>
        </div>
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Portfolio company</label>
            <select className="form-select w-64" value={portcoId} onChange={e => setPortcoId(e.target.value)}>
              {portcos.length === 0 && <option value="">No portfolio companies yet</option>}
              {portcos.map(p => <option key={p.portco_id} value={p.portco_id}>{p.portco_name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Reporting period</label>
            <input className="form-input w-40" placeholder="e.g. FY2026 or Q1 2026" value={period} onChange={e => setPeriod(e.target.value)} />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Source currency</label>
            <select className="form-select w-32" value={currency} onChange={e => setCurrency(e.target.value)}>
              {currencies.map(c => <option key={c.code} value={c.code}>{c.code}</option>)}
            </select>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Step 2 — upload */}
        <div className="card flex flex-col">
          <div className="flex items-center justify-between mb-4">
            <h3 className="card-title">
              <FileSpreadsheet className="w-5 h-5 text-gold-500" />
              2 · Upload the vendor financials
            </h3>
            <span className="badge badge-slate">CSV</span>
          </div>
          <label className="flex flex-col items-center justify-center border-2 border-dashed border-slate-300 rounded-xl p-8 cursor-pointer hover:border-brand-400 transition-colors">
            <Upload className="w-8 h-8 text-slate-300 mb-2" />
            <span className="text-sm text-slate-600 font-medium">
              {parseBusy ? 'Parsing…' : 'Drop or choose a CSV file'}
            </span>
            <span className="text-xs text-slate-400 mt-1">P&L or trial balance export — one account per row, with an Amount column</span>
            <input
              type="file" accept=".csv" className="hidden"
              disabled={parseBusy}
              onChange={e => { const f = e.target.files?.[0]; if (f) parseFile(f); e.target.value = ''; }}
            />
          </label>
          {parseBusy && <div className="flex items-center gap-2 text-sm text-slate-500 mt-3"><Loader2 className="w-4 h-4 animate-spin" /> Reading the statement…</div>}

          {fileInfo && (
            <p className="text-xs text-slate-500 mt-3">
              <b>{fileInfo.name}</b> · {fileInfo.rows} rows · {lineItems.length} line items · raw net {fmtUsd(fileInfo.total)}
            </p>
          )}

          {skipped.length > 0 && (
            <details className="mt-3 text-xs text-slate-500">
              <summary className="cursor-pointer">{skipped.length} row(s) skipped (totals, blanks, non-numeric)</summary>
              <ul className="mt-1 space-y-0.5">
                {skipped.map((s, i) => <li key={i}>“{s.row}” — {s.reason}</li>)}
              </ul>
            </details>
          )}

          {lineItems.length > 0 && (
            <div className="mt-4 border border-slate-200 rounded-lg overflow-hidden">
              <div className="max-h-72 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr className="text-left text-xs text-slate-500 uppercase tracking-wide">
                      <th className="px-3 py-2">Account</th>
                      <th className="px-3 py-2 text-right">Amount</th>
                      <th className="px-3 py-2 w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {lineItems.map((li, i) => (
                      <tr key={i} className="border-t border-slate-100">
                        <td className="px-3 py-1.5 text-slate-700">{li.account_name}</td>
                        <td className="px-3 py-1.5 text-right font-mono text-slate-800">{fmtUsd(li.amount)}</td>
                        <td className="px-3 py-1.5">
                          <button className="text-slate-300 hover:text-red-500" title="Remove row" onClick={() => removeRow(i)}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <button onClick={normalize} disabled={normalizing || !lineItems.length || !portcoId || !period} className="btn-primary mt-4 self-start disabled:opacity-50">
            <RefreshCw className={`w-4 h-4 ${normalizing ? 'animate-spin' : ''}`} />
            {normalizing ? 'Normalizing…' : `Normalize ${lineItems.length} line item${lineItems.length === 1 ? '' : 's'}`}
          </button>
        </div>

        {/* Step 3 — result */}
        <div className="card flex flex-col">
          <div className="flex items-center justify-between mb-4">
            <h3 className="card-title">
              <ShieldCheck className="w-5 h-5 text-emerald-600" />
              3 · Standard PE output
            </h3>
            {result && (
              <span className="badge badge-green">
                <CheckCircle2 className="w-3 h-3 mr-1" />
                {result.period}
              </span>
            )}
          </div>

          {result ? (
            <div className="space-y-4 flex-1">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {topMetrics.map(m => (
                  <div key={m.label} className={`rounded-lg p-3 ${m.highlight ? 'bg-emerald-50 border border-emerald-200' : 'bg-slate-50'}`}>
                    <p className="text-xs text-slate-500">{m.label}</p>
                    <p className={`mt-1 font-mono font-semibold ${m.highlight ? 'text-emerald-700' : 'text-navy-900'}`}>
                      {m.pct ? m.value : fmtUsd(m.value)}
                    </p>
                  </div>
                ))}
              </div>

              {result.currency_adjustments?.source_currency && result.currency_adjustments.source_currency !== 'USD' && (
                <p className="text-xs text-slate-500">
                  FX: {result.currency_adjustments.source_currency} → USD at {result.currency_adjustments.fx_rate} (revenue adjusted by {fmtUsd(result.currency_adjustments.adjusted_amount)})
                </p>
              )}

              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">Chart of accounts</p>
                <div className="max-h-56 overflow-y-auto border border-slate-200 rounded-lg">
                  <table className="w-full text-sm">
                    <tbody>
                      {Object.entries(result.line_items || {}).map(([k, v]) => (
                        <tr key={k} className="border-t border-slate-100 first:border-t-0">
                          <td className="px-3 py-1.5 text-slate-600">{prettyKey(k)}</td>
                          <td className="px-3 py-1.5 text-right font-mono text-navy-900">{fmtUsd(v)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {result.non_recurring_items?.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-xs font-semibold uppercase tracking-wider text-amber-600 mb-2">Non-recurring add-backs</p>
                  {result.non_recurring_items.map((adj, i) => (
                    <div key={i} className="flex items-center justify-between text-sm">
                      <span className="text-amber-700">{adj.description}</span>
                      <span className="font-mono text-amber-700">+{fmtUsd(adj.amount)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="flex-1 flex items-center justify-center text-center">
              <div className="text-slate-300">
                <Database className="w-12 h-12 mx-auto mb-3" />
                <p className="text-sm text-slate-400">Upload a CSV and click Normalize — the GAAP result appears here</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Step 4 — history & comparison */}
      <div className="card mt-4">
        <div className="card-header">
          <h3 className="card-title"><History className="w-5 h-5 text-brand-600" />4 · History &amp; period comparison</h3>
        </div>
        {history.length === 0 ? (
          <p className="text-sm text-slate-400">
            {portcoId ? 'No normalized periods recorded for this company yet.' : 'Choose a portfolio company to see its history.'}
          </p>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">Normalized periods</p>
              <div className="space-y-2">
                {history.map(h => (
                  <div key={h.id || h.period} className="flex items-center justify-between p-2.5 rounded-lg border border-slate-200 bg-slate-50/50 text-sm">
                    <span className="font-medium text-navy-900">{h.period}</span>
                    <span className="text-slate-500">
                      EBITDA <b className="font-mono text-navy-900">{fmtUsd(h.ebitda)}</b> · margin {h.ebitda_margin_pct}%
                    </span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">Compare two periods</p>
              <div className="flex items-center gap-2 mb-3">
                <select className="form-select w-32" value={cmpA} onChange={e => setCmpA(e.target.value)}>
                  {history.map(h => <option key={h.period} value={h.period}>{h.period}</option>)}
                </select>
                <ArrowRight className="w-4 h-4 text-slate-400" />
                <select className="form-select w-32" value={cmpB} onChange={e => setCmpB(e.target.value)}>
                  {history.map(h => <option key={h.period} value={h.period}>{h.period}</option>)}
                </select>
                <button className="btn-secondary" onClick={runCompare} disabled={compareBusy || !cmpA || !cmpB || cmpA === cmpB}>
                  {compareBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Compare'}
                </button>
              </div>
              {comparison && (
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr className="text-left text-xs text-slate-500 uppercase tracking-wide">
                        <th className="px-3 py-2">Metric</th>
                        <th className="px-3 py-2 text-right">{comparison.a}</th>
                        <th className="px-3 py-2 text-right">{comparison.b}</th>
                        <th className="px-3 py-2 text-right">Δ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {['total_revenue', 'total_cogs', 'gross_profit', 'total_opex', 'ebitda'].filter(k => comparison.changes[k]).map(k => {
                        const c = comparison.changes[k];
                        const up = c.absolute_change >= 0;
                        return (
                          <tr key={k} className="border-t border-slate-100">
                            <td className="px-3 py-1.5 text-slate-700">{prettyKey(k)}</td>
                            <td className="px-3 py-1.5 text-right font-mono text-slate-500">{fmtUsd(c.period_a_value)}</td>
                            <td className="px-3 py-1.5 text-right font-mono text-navy-900">{fmtUsd(c.period_b_value)}</td>
                            <td className={`px-3 py-1.5 text-right font-mono ${up ? 'text-emerald-600' : 'text-red-600'}`}>
                              {up ? '+' : ''}{fmtUsd(c.absolute_change)} ({c.percentage_change}%)
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
